import React, { useState, useEffect } from 'react';
import { Navigation } from './components/navigation';
import { Header } from './components/header';
import { Products } from './components/products';
import { Features } from './components/features';
import { Blog } from './components/blog';
import { Contact } from './components/contact';
import { PrivacyPolicy } from './components/privacyPolicy';
import { LocalHiderPrivacyPolicy } from './components/localHiderPrivacyPolicy';
import { AloraLanding } from './components/aloraLanding';
import { WebrosterLanding } from './components/webrosterLanding';
import { WebrosterAppLanding } from './components/webrosterAppLanding';
import { PredictorLanding } from './components/predictorLanding';
import { NimboLanding } from './components/nimboLanding';
import { TestamentoFacilLanding } from './components/testamentoFacilLanding';
import { LocalHiderLanding } from './components/localHiderLanding';
import { getGoogleTagId, initGoogleTag, trackGooglePageView } from './lib/googleTag';
import JsonData from './data/data.json';
import SmoothScroll from 'smooth-scroll';
import './App.css';

export const scroll = new SmoothScroll('a[href*="#"]', {
  speed: 800,
  speedAsDuration: true,
  easing: 'easeInOutCubic',
});

const syncHeadMetadata = ({ title, description, url, image }) => {
  document.title = title;

  const metaConfigs = [
    { attr: 'name', key: 'description', content: description },
    { attr: 'property', key: 'og:title', content: title },
    { attr: 'property', key: 'og:description', content: description },
    { attr: 'property', key: 'og:type', content: 'website' },
    { attr: 'property', key: 'og:url', content: url },
    { attr: 'property', key: 'og:image', content: image },
    { attr: 'name', key: 'twitter:card', content: 'summary_large_image' },
    { attr: 'name', key: 'twitter:title', content: title },
    { attr: 'name', key: 'twitter:description', content: description },
    { attr: 'name', key: 'twitter:image', content: image },
  ];

  metaConfigs.forEach(({ attr, key, content }) => {
    let element = document.head.querySelector(`meta[${attr}="${key}"]`);

    if (!element) {
      element = document.createElement('meta');
      element.setAttribute(attr, key);
      document.head.appendChild(element);
    }

    element.setAttribute('content', content);
  });

  let canonical = document.head.querySelector('link[rel="canonical"]');

  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    document.head.appendChild(canonical);
  }

  canonical.setAttribute('href', url);
};

const App = () => {
  const pathname = window.location.pathname.replace(/\/$/, '');
  const isAloraPrivacyRoute      = pathname === '/privacy_policy_alora';
  const isLocalHiderPrivacyRoute = pathname === '/privacy_policy_local_hider';
  const isPrivacyPolicyRoute  = isAloraPrivacyRoute || isLocalHiderPrivacyRoute;
  const isAloraRoute          = pathname === '/alora';
  const isWebrosterRoute      = pathname === '/webroster';
  const isWebrosterAppRoute   = pathname === '/webroster-app';
  const isPredictorRoute      = pathname === '/predictor';
  const isNimboRoute          = pathname === '/nimbo';
  const isMiquinielaRoute     = pathname === '/miquiniela';
  const isTestamentoFacilRoute = pathname === '/testamento-facil';
  const isLocalHiderRoute      = pathname === '/local-hider';
  const isProductRoute = isAloraRoute || isWebrosterRoute || isWebrosterAppRoute || isPredictorRoute || isNimboRoute || isMiquinielaRoute || isTestamentoFacilRoute || isLocalHiderRoute;

  const [landingPageData, setLandingPageData] = useState(null);

  useEffect(() => {
    if (isPrivacyPolicyRoute || isProductRoute) {
      return;
    }
    setLandingPageData(JsonData);
  }, [isProductRoute, isPrivacyPolicyRoute]);

  useEffect(() => {
    if (isPrivacyPolicyRoute || isProductRoute) {
      return;
    }

    const pageTitle = 'Mindware | Digital Product Studio';
    const pageDescription =
      'Mindware is a digital product studio based in Cancún. We design and build software end-to-end — from strategy and UX to development and launch.';
    const pageUrl = `${window.location.origin}/`;
    const previewImage = `${window.location.origin}/img/webroster1.png`;

    syncHeadMetadata({
      title: pageTitle,
      description: pageDescription,
      url: pageUrl,
      image: previewImage,
    });
  }, [isProductRoute, isPrivacyPolicyRoute]);

  useEffect(() => {
    const googleTagId = getGoogleTagId();

    if (!googleTagId) {
      return;
    }

    initGoogleTag(googleTagId);
    trackGooglePageView(googleTagId);
  }, [pathname]);

  if (isAloraPrivacyRoute)      return <PrivacyPolicy />;
  if (isLocalHiderPrivacyRoute) return <LocalHiderPrivacyPolicy />;
  if (isAloraRoute)          return <AloraLanding />;
  if (isWebrosterRoute)      return <WebrosterLanding />;
  if (isWebrosterAppRoute)   return <WebrosterAppLanding />;
  if (isPredictorRoute)      return <PredictorLanding />;
  if (isNimboRoute)          return <NimboLanding />;
  if (isTestamentoFacilRoute) return <TestamentoFacilLanding />;
  if (isLocalHiderRoute)      return <LocalHiderLanding />;
  if (isMiquinielaRoute) {
    window.location.replace('https://miquiniela.com.mx');
    return null;
  }

  if (!landingPageData) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <>
      <Navigation />
      <Header data={landingPageData.Header} />
      <main>
        <Products data={landingPageData.Products} />
        <Features data={landingPageData.Services} />
        <Blog data={landingPageData.Blog} />
      </main>
      <Contact data={landingPageData.Contact} />
    </>
  );
};

export default App;
